import * as React from 'react';
import { SignupStep1View, SignupStep1ViewProps } from './SignupStep1.view';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  Network,
  RegisterEmailAvailableLazyQueryHookResult,
  RegisterEmailAvailableQueryVariables,
  SocialOAuthProvider,
  StartSocialOAuthMutation
} from '../../../models/graphql';
import { useForm, RegisterOptions } from 'react-hook-form';
import { FormOptions, ValidationPatterns } from '../../../constants/form';
import { ValidationMessages } from '../../../constants/validation-messages';
import { AuthContextEnum } from '../../../constants/auth';
import { IInvitation } from '../../../models/auth';
import { useReactiveVar } from '@apollo/client';
import { UseFormProps } from 'react-hook-form/dist/types/form';
import { invitationDetailsVar } from '../../../providers/AppProvider';

export type SignupStep1ControllerProps = Omit<SignupStep1ViewProps, 'emailRules' | 'formInstance' | 'onSubmit'> & {
  registerEmailAvailable: RegisterEmailAvailableLazyQueryHookResult[0];
  setStep: Dispatch<SetStateAction<1 | 2>>;
  startSocialOAuthData?: StartSocialOAuthMutation['startSocialOAuth'];
  signupEmail?: string;
  registerEmailAvailableResult?: boolean;
  setEmailForRegistration: Dispatch<SetStateAction<string>>;
};

export type RegisterEmailAvailableResponseType = {
  available: boolean | null;
  error: string | JSX.Element;
};

export const SignupStep1Controller = (props: SignupStep1ControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const invitation: IInvitation | null = useReactiveVar(invitationDetailsVar);

  const [defaultFormValues, setDefaultFormValues] = useState<RegisterEmailAvailableQueryVariables>({
    email: props.signupEmail || ''
  });

  const formOptions = { ...FormOptions } as UseFormProps<RegisterEmailAvailableQueryVariables>;
  const formInstance = useForm<RegisterEmailAvailableQueryVariables>({
    ...formOptions,
    defaultValues: defaultFormValues
  });
  const emailRules: RegisterOptions = {
    required: ValidationMessages.email.required,
    pattern: {
      value: ValidationPatterns.email,
      message: ValidationMessages.email.invalid
    }
  };

  const onSubmit = (submittedFormData: RegisterEmailAvailableQueryVariables) => {
    props.registerEmailAvailable({ variables: { email: submittedFormData.email } });
  };

  // we trigger step update if we receive registerEmailAvailable data.
  // this will trigger render of the signup controller, where we chose which step component to render
  useEffect(() => {
    if (props.registerEmailAvailableResult && props.signupEmail) {
      props.setStep(2);
      props.setEmailForRegistration(props.signupEmail);
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [props.registerEmailAvailableResult, props.signupEmail]);

  // watch for the props.signupEmail change and update default values is needed.
  // We need to put this inside useEffect since we reset form values
  useEffect(() => {
    if (props.signupEmail && props.signupEmail !== defaultFormValues.email) {
      formInstance.setValue('email', props.signupEmail, { shouldValidate: true });
      setDefaultFormValues({ email: props.signupEmail || '' });
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [props.signupEmail, defaultFormValues]);

  // If we receive socialData, we want to set session storage, since we'll redirect from the react app
  // we'll grab the session storage once we came back from socialOAuth and set reactive var in src/apollo/Cache.tsx file
  if (props.startSocialOAuthData) {
    const { socialAuthToken, redirectUrl } = props.startSocialOAuthData;
    sessionStorage.setItem(
      'socialOAuth',
      JSON.stringify({
        socialAuthToken,
        context: AuthContextEnum.signup,
        oAuthProvider: SocialOAuthProvider.FACEBOOK,
        network: Network.FACEBOOK
      })
    );
    if (invitation) {
      sessionStorage.setItem('invitation', JSON.stringify(invitation));
    }

    window.location.href = redirectUrl;
  }

  const viewProps: SignupStep1ViewProps = {
    ...props,
    emailRules,
    formInstance,
    onSubmit
  };

  return <SignupStep1View {...viewProps} />;
};
